import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { store } from './store/store';

// Layout
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import Landing from './pages/Landing';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import About from './pages/About';
import Pricing from './pages/Pricing';
import Features from './pages/Features';
import JobManagement from './pages/features/JobManagement';
import Invoicing from './pages/features/Invoicing';
import VehicleHealthChecks from './pages/features/VehicleHealthChecks';
import CustomerManagement from './pages/features/CustomerManagement';
import CompareGarageHive from './pages/compare/GarageHive';
import CompareAutoLeap from './pages/compare/AutoLeap';
import CompareTechMan from './pages/compare/TechMan';

// Auth Pages
import Login from './pages/auth/Login';
// import Register from './pages/auth/Register'; // Disabled registration

// Main Pages
import Customers from './pages/customers/Customers';
import Booking from './pages/booking/Booking';
import PublicBookingForm from './pages/booking/PublicBookingForm';
import BookingSuccess from './pages/booking/BookingSuccess';
import BookingTerms from './pages/booking/BookingTerms';
import Technicians from './pages/technicians/Technicians';
import JobBoard from './pages/job-board/JobBoard';
import AdminBoard from './pages/admin-board/AdminBoard';
import UnroutedEmails from './pages/admin-board/UnroutedEmails';
import PaymentPending from './pages/payments/PaymentPending';
import PaymentPaid from './pages/payments/PaymentPaid';
import PaymentCheckout from './pages/payment/PaymentCheckout';
import PaymentCheckoutEnhanced from './pages/payment/PaymentCheckoutEnhanced';
import PaymentSuccess from './pages/payment/PaymentSuccess';
import PaymentFailed from './pages/payment/PaymentFailed';
import Chat from './pages/chat/Chat';
import Alerts from './pages/alerts/Alerts';

// Invoicing Pages
import Invoices from './pages/invoicing/Invoices';
import OrganizationSettings from './pages/invoicing/OrganizationSettings';

// Docs Page
import Docs from './pages/docs/Docs';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import TechnicianProfile from './pages/profile/TechnicianProfile';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2c3e50', // Dark charcoal grey - professional garage aesthetic
      light: '#34495e',
      dark: '#1a252f',
    },
    secondary: {
      main: '#ff6b35', // Safety orange - automotive warning/high-visibility
      light: '#ff8c5f',
      dark: '#e65100',
    },
    success: {
      main: '#28a745', // Green - traffic light
    },
    warning: {
      main: '#ffc107', // Amber - traffic light
    },
    error: {
      main: '#dc3545', // Red - traffic light
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            {/* Public Landing */}
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/features/job-management" element={<JobManagement />} />
            <Route path="/features/invoicing" element={<Invoicing />} />
            <Route path="/features/vehicle-health-checks" element={<VehicleHealthChecks />} />
            <Route path="/features/customer-management" element={<CustomerManagement />} />
            <Route path="/compare/garagehive" element={<CompareGarageHive />} />
            <Route path="/compare/autoleap" element={<CompareAutoLeap />} />
            <Route path="/compare/techman" element={<CompareTechMan />} />

            {/* Auth Routes */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route path="login" element={<Login />} />
              {/* <Route path="register" element={<Register />} /> */} {/* Disabled registration */}
            </Route>

            {/* Public Payment Routes */}
            <Route path="/payment/:token" element={<PaymentCheckoutEnhanced />} />
            <Route path="/payment/:token/success" element={<PaymentSuccess />} />
            <Route path="/payment/:token/failed" element={<PaymentFailed />} />

            {/* Public Booking Routes */}
            <Route path="/book/:orgCode" element={<PublicBookingForm />} />
            <Route path="/book/:orgCode/terms" element={<BookingTerms />} />
            <Route path="/book/:orgCode/success" element={<BookingSuccess />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
              <Route path="customers/*" element={<ProtectedAdminRoute allowGarageManager><Customers /></ProtectedAdminRoute>} />
              <Route path="booking/*" element={<ProtectedAdminRoute allowGarageManager><Booking /></ProtectedAdminRoute>} />
              <Route path="technicians/*" element={<ProtectedAdminRoute allowGarageManager><Technicians /></ProtectedAdminRoute>} />
              <Route path="job-board/*" element={<JobBoard />} />
              <Route path="admin-board/*" element={<ProtectedAdminRoute allowGarageManager><AdminBoard /></ProtectedAdminRoute>} />
              <Route path="payments/pending/*" element={<ProtectedAdminRoute allowGarageManager><PaymentPending /></ProtectedAdminRoute>} />
              <Route path="payments/paid/*" element={<ProtectedAdminRoute allowGarageManager><PaymentPaid /></ProtectedAdminRoute>} />
              <Route path="invoices/*" element={<ProtectedAdminRoute allowGarageManager><Invoices /></ProtectedAdminRoute>} />
              <Route path="organization-settings/*" element={<ProtectedAdminRoute><OrganizationSettings /></ProtectedAdminRoute>} />
              <Route path="profile/*" element={<TechnicianProfile />} />
              <Route path="chat/*" element={<ProtectedAdminRoute><Chat /></ProtectedAdminRoute>} />
              <Route path="alerts/*" element={<ProtectedAdminRoute><Alerts /></ProtectedAdminRoute>} />
              <Route path="docs/*" element={<ProtectedAdminRoute><Docs /></ProtectedAdminRoute>} />
              <Route path="unrouted-emails" element={<ProtectedAdminRoute><UnroutedEmails /></ProtectedAdminRoute>} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<RoleBasedRedirect />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
